import React from 'react';
import { StatsCard } from '../../../../components/shared/StatsCard';
import {
  FileText,
  AlertTriangle,
  Clock,
  AlertCircle,
  Users
} from 'lucide-react';

/**
 * DSR KPI Row Component
 * Displays 5 counter cards:
 * 1. Open requests (neutral)
 * 2. Overdue (red)
 * 3. Due ≤ 7 days (amber)
 * 4. Missing deadline (amber)
 * 5. 3rd party / unverified (amber)
 */
export function DSRKPIRow({ dsrs = [] }) {
  // Helper to determine deadline status from days remaining
  const getDeadlineStatus = (daysRemaining, deadline) => {
    if (!deadline) return 'unset';
    if (daysRemaining < 0) return 'overdue';
    if (daysRemaining <= 7) return 'due_soon';
    return 'on_track';
  };

  // Calculate counters
  let openCount = 0;
  let overdueCount = 0;
  let dueSoonCount = 0;
  let missingDeadlineCount = 0;
  let thirdPartyUnverifiedCount = 0;

  dsrs.forEach(dsr => {
    const isOpen = dsr.status && !['responded', 'closed', 'withdrawn', 'refused'].includes(dsr.status);

    if (isOpen) {
      openCount++;

      // Use API-provided deadline and days_remaining
      const status = getDeadlineStatus(dsr.days_remaining, dsr.deadline);

      // Count overdue
      if (status === 'overdue') {
        overdueCount++;
      }

      // Count due soon
      if (status === 'due_soon') {
        dueSoonCount++;
      }

      // Count missing deadline
      if (status === 'unset') {
        missingDeadlineCount++;
      }

      // Count 3rd party unverified
      const isThirdParty = dsr.requester?.is_third_party || dsr.requester?.is_agent;
      const notAuthorized = !dsr.requester?.agent_authorization && !dsr.requester?.third_party_authorization;
      if (isThirdParty && notAuthorized) {
        thirdPartyUnverifiedCount++;
      }
    }
  });

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <StatsCard
        title="Open requests"
        value={openCount}
        icon={FileText}
        borderColor="border-l-blue-500"
        iconBgColor="bg-blue-100 dark:bg-blue-950"
        iconColor="text-blue-600 dark:text-blue-400"
        subtitle={openCount === 1 ? 'request' : 'requests'}
      />
      <StatsCard
        title="Overdue"
        value={overdueCount}
        icon={AlertTriangle}
        borderColor="border-l-red-500"
        iconBgColor="bg-red-100 dark:bg-red-950"
        iconColor="text-red-600 dark:text-red-400"
        valueColor={overdueCount > 0 ? "text-red-600 dark:text-red-400" : undefined}
        subtitle="past deadline"
      />
      <StatsCard
        title="Due ≤ 7 days"
        value={dueSoonCount}
        icon={Clock}
        borderColor="border-l-amber-500"
        iconBgColor="bg-amber-100 dark:bg-amber-950"
        iconColor="text-amber-600 dark:text-amber-400"
        valueColor={dueSoonCount > 0 ? "text-amber-600 dark:text-amber-400" : undefined}
        subtitle="days remaining"
      />
      <StatsCard
        title="Missing deadline"
        value={missingDeadlineCount}
        icon={AlertCircle}
        borderColor="border-l-amber-500"
        iconBgColor="bg-amber-100 dark:bg-amber-950"
        iconColor="text-amber-600 dark:text-amber-400"
        valueColor={missingDeadlineCount > 0 ? "text-amber-600 dark:text-amber-400" : undefined}
        subtitle="not set"
      />
      <StatsCard
        title="3rd party / unverified"
        value={thirdPartyUnverifiedCount}
        icon={Users}
        borderColor="border-l-amber-500"
        iconBgColor="bg-amber-100 dark:bg-amber-950"
        iconColor="text-amber-600 dark:text-amber-400"
        valueColor={thirdPartyUnverifiedCount > 0 ? "text-amber-600 dark:text-amber-400" : undefined}
        subtitle="auth. pending"
      />
    </div>
  );
}

export default DSRKPIRow;
